import $ from 'jquery';
import axios from "axios";
import { formatFileSize, localePrefix } from "./helpers";

// Shrine's versions plugin nests an image's metadata under `original`; attachments and freshly
// uploaded files carry it at the top level. Both shapes reach the browser, so every read goes
// through here.
const fileMetadata = file => (file && (file.original?.metadata ?? file.metadata)) || {};

const FILE_KINDS = [
  { kind: 'image', icon: 'mdi-file-image-outline', matches: mimeType => /^image\//.test(mimeType) },
  { kind: 'pdf', icon: 'mdi-file-pdf-box', matches: mimeType => mimeType === 'application/pdf' },
];
const FILE_KIND_FALLBACK = { kind: 'file', icon: 'mdi-file-document-outline' };

// Row presentation for the media-kit list. The Handlebars template carries no logic, so the icon
// class, the kind modifier and the "size · width×height" line are resolved here. Page counts are
// not stored anywhere, hence never shown.
//
// The line's two halves are handed over separately as well: Overpass draws `·` with no advance
// width at all, so a literal ' · ' renders as "329 KB ·1024×682" — the row puts the separator in
// its own element and lets CSS space it (`.media-kit-file-meta-sep`). `meta` stays a plain string
// for anything that wants the line as text, and is what decides whether the line is rendered.
const describeFile = metadata => {
  const mimeType = metadata.mime_type || '';
  const { kind, icon } = FILE_KINDS.find(entry => entry.matches(mimeType)) || FILE_KIND_FALLBACK;
  const dimensions = metadata.width && metadata.height ? `${metadata.width}×${metadata.height}` : '';
  const sizeLabel = formatFileSize(metadata.size);

  return {
    kind,
    icon,
    size: metadata.size,
    mimeType,
    sizeLabel,
    dimensions,
    meta: [sizeLabel, dimensions].filter(Boolean).join(' · '),
  };
};

export class API {
  static endpoints = {
    release: {
      create: {url: '{locale}/releases', method: 'post'},
      update: {url: '{locale}/releases/{id}', method: 'put'},
      finalize: {url: '{locale}/releases/{id}/checkout', method: 'put'},
      lock: {url: '{locale}/releases/{id}/lock', method: 'put'},
      unlock: {url: '{locale}/releases/{id}/unlock', method: 'put'},
      notifications: {url: '{releaseUrl}/release_notifications', method: 'post'},
      translate: {url: '{locale}/releases/translate', method: 'post'},
    },
    users: {
      login: {url: '{locale}/users/sign_in', method: 'post'},
      signup: {url: '{locale}/users', method: 'post'},
      resetPassword: {url: '{locale}/users/password', method: 'post'},
    },
    uploads: {
      companionUrl: '/',
    },
  };

  static parseFile = {
    fromPageData: input => {
      const metadata = fileMetadata(input.file);
      const filename = metadata.filename || input.file?.metadata?.filename || '';

      return {
        file_id: input.file_id,
        id: input.id,
        url: input.url,
        name: filename,
        ...describeFile(metadata),
        description: input.description || '',
        content: input.content,
        apiData: {
          description: input.description || '',
          id: input.id,
          file: input.file,
          // url: input.url,
        }
      }
    },
    fromUppy: input => {
      const keyData = input.meta.key.match(/^([^/]*)\/(.*)$/);
      const metadata = {
        size: input.size,
        filename: input.name,
        mime_type: input.type,
      };

      return {
        uppyId: input.id,
        file_id: keyData[2],
        id: null,
        url: null,
        name: input.name,
        ...describeFile(metadata),
        description: '',
        apiData: {
          description: '',
          file: {
            id: keyData[2],
            storage: keyData[1],
            metadata,
          },
        },
      };
    },
    fromUpdateAPI: input => {
      return API.parseFile.fromPageData(input);
    },
    // A file with an id is already a row on the server: only its description travels, and its
    // media-kit membership is decided by the separate `media_kit` list (an id per member, which is
    // what lets a de-selected file be cleared). A file without one is created by this request, so
    // its flags have to ride along with it — there is no id to name it by yet.
    //
    // `content` is read out of the Shrine file JSON (UpdateReleaseService#assign_attachments);
    // `media_kit` is read from the top level of the entry, which is also where the controller
    // permits it (`images: [:id, :file, :description, :media_kit]`). Nesting it inside the file JSON
    // reached the server but nothing there ever read it, so every freshly uploaded file was
    // persisted with media_kit = false and only became a kit member on some *later* save that could
    // finally name it by id — the last upload before leaving the page never got one.
    forUpdateAPI: file => {
      return {
        ...file.apiData,
        file: JSON.stringify({
          ...file.apiData.file,
          ...(!file.id && { content: file.isContent }),
        }),
        description: file.description,
        ...(!file.id && { media_kit: !!file.inMediaKit }),
      };
    },
  };

  static call = ({endpoint, params, data, suffix = ''}) => {
    return axios({
      url: this.prepareUrl(endpoint.url, params) + (suffix ? `?${suffix}` : ''),
      method: endpoint.method || 'get',
      data,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content')
      },
    });
  };

  static prepareUrl = (pattern, params) => {
    const paramsWithLocale = {
      locale: localePrefix(),
      ...params,
    };

    let url = pattern;
    Object.entries(paramsWithLocale).forEach(([key, value]) => {
      url = url.replace(new RegExp(`\{${key}\}`, 'g'), value);
    });
    return url;
  };
};
